import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import { IMqttMessage } from "ngx-mqtt";
import { Observable, BehaviorSubject, Subscription } from "rxjs";
import { debounceTime, filter, map, withLatestFrom } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { Settings } from "src/model/class/settings";
import { AccessResult } from "src/components/shared-components/enum/enumAccessControl";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { BIGDeviceType } from "src/components/shared-components/enum/enumDevice";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";

@Injectable({
  providedIn: "root",
})
export class TagService {
  private _logContext: string = "tag.service";
  private _serverSetting: Settings = null;
  private _serverSettingSubscription: Subscription = null;
  constructor(
    private _store: Store,
    private _loggerService: LoggerService
  ) {
    this.loadServerSettings();
  }

  /**
   * Load server settings
   */
  public loadServerSettings(): void {
    this._serverSettingSubscription = this._store
      .select(serverSettings)
      .subscribe((settings: Settings) => {
        this._serverSetting = settings;
      });
  }

  /**
   * Calculate the block index for read/write for myto by device type
   */
  public calculateBlockIndexForMytoByDeviceType(
    deviceType: BIGDeviceType
  ): number {
    let block: number = -1;
    let sector: number = -1;
    if (deviceType == BIGDeviceType.ABB_MILLENIUM) {
      block = this.getBlockABB();
      sector = this.getSectorABB();
    }
    if (deviceType == BIGDeviceType.VIMAR_WELL_CONTACT_PLUS) {
      block = this.getBlockWCP();
      sector = this.getSectorWCP();
    }
    if (deviceType == BIGDeviceType.BIG_MIFARE) {
      block = this.getBlockBIGMifare();
      sector = this.getSectorBIGMifare();
    }
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "calculateBlockIndexForMytoByDeviceType()",
      "Try to read block " + block + " sector " + sector
    );
    return block + sector * 4;
  }

  /**
   * Get key for read by device type
   */
  public getKeyByDeviceType(deviceType: BIGDeviceType): string {
    let key: string = "";
    if (deviceType == BIGDeviceType.ABB_MILLENIUM) {
      key = this.calculateABBKeyA();
    }
    if (deviceType == BIGDeviceType.BIG_MIFARE) {
      key = this.calculateBIGVirtualMIFAREKeyA();
    }
    if (deviceType == BIGDeviceType.VIMAR_WELL_CONTACT_PLUS) {
      key = this.calculateVimarWCPKeyA();
    }
    return key;
  }

  /**
   * Recover key A for ABB
   * @returns
   */
  public calculateABBKeyA(): string {
    return AppSettings.ABB_MILLENIUM_DEFAULT_KEY_A;
  }

  /**
   * Recover key B for ABB
   * @returns
   */
  public calculateABBKeyB(): string {
    return AppSettings.ABB_MILLENIUM_DEFAULT_KEY_B;
  }

  /**
   * Recover keyA from serverSettings for VIMAR
   *
   * @returns keyA formatted
   */
  public calculateVimarWCPKeyA(): string {
    let uppKey = (
      this._serverSetting["CardAccessControlUpperKeyA"] * 1
    ).toString(16);
    let midKey = (this._serverSetting["CardAccessControlMidKeyA"] * 1).toString(
      16
    );
    let lowKey = (
      this._serverSetting["CardAccessControlLowerKeyA"] * 1
    ).toString(16);
    return (uppKey + midKey + lowKey).toString().toUpperCase();
  }

  /**
   * Recover keyB from serverSettings for VIMAR
   *
   * @returns keyB
   */
  public calculateVimarWCPKeyB(): string {
    let uppKey = (
      this._serverSetting["CardAccessControlUpperKeyB"] * 1
    ).toString(16);
    let midKey = (this._serverSetting["CardAccessControlMidKeyB"] * 1).toString(
      16
    );
    let lowKey = (
      this._serverSetting["CardAccessControlLowerKeyB"] * 1
    ).toString(16);
    return (uppKey + midKey + lowKey).toString().toUpperCase();
  }

  /**
   * Recover keyA from serverSettings for BIG Virtual MIFARE
   *
   * @returns keyA formatted
   */
  public calculateBIGVirtualMIFAREKeyA(): string {
    let uppKey = (this._serverSetting["CardMyToUpperKeyA"] * 1).toString(16);
    let midKey = (this._serverSetting["CardMyToMidKeyA"] * 1).toString(16);
    let lowKey = (this._serverSetting["CardMyToLowerKeyA"] * 1).toString(16);
    return (uppKey + midKey + lowKey).toString().toUpperCase();
  }

  /**
   * Recover keyB from serverSettings for BIG Virtual MIFARE
   *
   * @returns keyB
   */
  public calculateBIGVirtualMIFAREKeyB(): string {
    let uppKey = (this._serverSetting["CardMyToUpperKeyB"] * 1).toString(16);
    let midKey = (this._serverSetting["CardMyToMidKeyB"] * 1).toString(16);
    let lowKey = (this._serverSetting["CardMyToLowerKeyB"] * 1).toString(16);
    return (uppKey + midKey + lowKey).toString().toUpperCase();
  }

  /**
   * Return sector to write/read for ABB
   */
  public getSectorABB(): number {
    return AppSettings.ABB_MILLENIUM_DEFAULT_SECTOR;
  }

  /**
   * Return block to write/read for ABB
   */
  public getBlockABB(): number {
    return AppSettings.ABB_MILLENIUM_DEFAULT_BLOCK;
  }

  /**
   * Return sector to write/read for ABB
   */
  public getSectorWCP(): number {
    return 1;
  }

  /**
   * Return block to write/read for ABB
   */
  public getBlockWCP(): number {
    return 0;
  }

  /**
   * Return sector to write/read for ABB
   */
  public getSectorBIGMifare(): number {
    return this._serverSetting["CardMyToSector"];
  }

  /**
   * Return block to write/read for ABB
   */
  public getBlockBIGMifare(): number {
    return this._serverSetting["CardMyToBlock"];
  }
}
